/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { dateUtils } from '@opengeoweb/shared';
import { fakeNafConfig } from './fakeNafConfig';
import type {
  NafLocation,
  NSC,
  TafConfigLocationDynamicPeriod,
  TafFromBackend,
} from '../../../types';
import type { WeatherPhenomena } from '../../weatherPhenomena';

// Used to mimic an authenticated user
export const MOCK_USERNAME = 'user.name';

const now = dateUtils.utc();

const currentDate = dateUtils.dateToString(
  dateUtils.utc(),
  dateUtils.DATE_FORMAT_YEAR,
)!;

const dateAdd = (refDateAsString: string, hours: number): string => {
  const newDate = dateUtils.add(dateUtils.utc(refDateAsString), { hours });
  const formattedDate = dateUtils.dateToString(
    newDate,
    dateUtils.DATE_FORMAT_UTC,
  )!;
  return formattedDate;
};

function getDateRange(
  locationConfig: TafConfigLocationDynamicPeriod,
  index: number,
  addDays = 0,
): string[] {
  const baseDate = dateUtils.add(now, { days: addDays });
  const start = dateUtils.set(
    baseDate,
    {
      hours: locationConfig.valid_start_times[index],
      minutes: 0,
      seconds: 0,
    },
    true,
  );

  const end = dateUtils.add(start, {
    hours: locationConfig.valid_durations[index],
  });

  return [
    dateUtils.dateToString(start, dateUtils.DATE_FORMAT_UTC, false)!,
    dateUtils.dateToString(end, dateUtils.DATE_FORMAT_UTC, false)!,
  ];
}

interface LocationDates {
  currentValidDateStart: string;
  currentValidDateEnd: string;
  upcomingValidDateStart: string;
  upcomingValidDateEnd: string;
}

function withLocationDates(
  location: NafLocation,
  cb: (dates: LocationDates) => TafFromBackend,
): TafFromBackend {
  const locationConfig = fakeNafConfig.locations[
    location
  ] as TafConfigLocationDynamicPeriod;
  let currentIndex = locationConfig.valid_start_times.findLastIndex(
    (startTime) => now.getUTCHours() >= startTime,
  );

  const currentIsYesterday = currentIndex === -1;
  if (currentIsYesterday) {
    currentIndex = locationConfig.valid_start_times.length - 1;
  }

  const upcomingIndex =
    (currentIndex + 1) % locationConfig.valid_start_times.length;

  // If upcoming start time is earlier than or equal to current start time, we've wrapped to the next day
  const upcomingWrapsToNextDay =
    locationConfig.valid_start_times[upcomingIndex] <=
    locationConfig.valid_start_times[currentIndex];

  const [currentValidDateStart, currentValidDateEnd] = getDateRange(
    locationConfig,
    currentIndex,
    currentIsYesterday ? -1 : 0,
  );
  const [upcomingValidDateStart, upcomingValidDateEnd] = getDateRange(
    locationConfig,
    upcomingIndex,
    upcomingWrapsToNextDay ? 1 : 0,
  );

  return cb({
    currentValidDateStart,
    currentValidDateEnd,
    upcomingValidDateStart,
    upcomingValidDateEnd,
  });
}

export const fakeNafList: TafFromBackend[] = [
  withLocationDates(
    'EHAK',
    ({ upcomingValidDateStart, upcomingValidDateEnd }) => ({
      creationDate: upcomingValidDateStart,
      canbe: ['DRAFTED', 'DISCARDED', 'PUBLISHED'],
      editor: MOCK_USERNAME,
      taf: {
        uuid: '5f9583fjdf0f337925',
        type: 'NAF',
        baseTime: upcomingValidDateStart,
        validDateStart: upcomingValidDateStart,
        validDateEnd: upcomingValidDateEnd,
        location: 'EHAK',
        status: 'NEW',
        messageType: 'ORG',
        baseForecast: {
          valid: {
            start: upcomingValidDateStart,
            end: upcomingValidDateEnd,
          },
        },
        remark: 'THIS IS A REMARK',
      },
    }),
  ),
  withLocationDates(
    'EHFD',
    ({ upcomingValidDateStart, upcomingValidDateEnd }) => ({
      creationDate: upcomingValidDateStart,
      canbe: ['DRAFTED', 'DISCARDED', 'PUBLISHED'],
      editor: null!,
      taf: {
        uuid: '5f9583fjd111137925',
        type: 'NAF',
        baseTime: upcomingValidDateStart,
        validDateStart: upcomingValidDateStart,
        validDateEnd: upcomingValidDateEnd,
        location: 'EHFD',
        status: 'DRAFT',
        messageType: 'ORG',
        baseForecast: {
          valid: {
            start: upcomingValidDateStart,
            end: upcomingValidDateEnd,
          },
          wind: { direction: 180, speed: 10, unit: 'KT' },
          cavOK: true,
        },
      },
    }),
  ),
  withLocationDates(
    'EHKV',
    ({ upcomingValidDateStart, upcomingValidDateEnd }) => ({
      creationDate: upcomingValidDateStart,
      canbe: ['CANCELLED', 'AMENDED', 'DISCARDED'],
      editor: null!,
      taf: {
        uuid: '754905485',
        type: 'NAF',
        baseTime: upcomingValidDateStart,
        previousId: '5f9583fjdf45ff4330f337925',
        validDateStart: upcomingValidDateStart,
        validDateEnd: upcomingValidDateEnd,
        previousValidDateStart: upcomingValidDateStart,
        previousValidDateEnd: upcomingValidDateEnd,
        location: 'EHKV',
        status: 'DRAFT_AMENDED',
        messageType: 'AMD',
        baseForecast: {
          valid: {
            start: upcomingValidDateStart,
            end: upcomingValidDateEnd,
          },
          visibility: { range: 600, unit: 'M' },
          wind: { direction: 180, speed: 10, unit: 'KT' },
          weather: { weather1: 'SN' as WeatherPhenomena },
          cloud: { cloud1: { coverage: 'BKN', height: 35 } },
        },
        changeGroups: [
          {
            change: 'TEMPO',
            probability: 'PROB30',
            valid: {
              start: dateAdd(upcomingValidDateStart, 3),
              end: dateAdd(upcomingValidDateStart, 4),
            },
            visibility: { range: 100, unit: 'M' },
            cloud: {
              cloud1: { verticalVisibility: 5 },
            },
          },
        ],
      },
    }),
  ),
  withLocationDates(
    'EHKV',
    ({ upcomingValidDateStart, upcomingValidDateEnd }) => ({
      creationDate: upcomingValidDateStart,
      canbe: ['CANCELLED', 'AMENDED', 'CORRECTED'],
      editor: MOCK_USERNAME,
      taf: {
        uuid: '43545345345',
        type: 'NAF',
        issueDate: upcomingValidDateStart,
        baseTime: `${currentDate}T18:00:00Z`,
        validDateStart: `${currentDate}T18:00:00Z`,
        validDateEnd: upcomingValidDateEnd,
        previousId: 'jkdjkdfjkgdf',
        previousValidDateStart: upcomingValidDateStart,
        previousValidDateEnd: upcomingValidDateEnd,
        location: 'EHKV',
        status: 'CORRECTED',
        messageType: 'COR',
        baseForecast: {
          valid: {
            start: upcomingValidDateStart,
            end: upcomingValidDateEnd,
          },
          wind: { direction: 350, speed: 46, gust: 67, unit: 'KT' },
          cavOK: true,
        },
        changeGroups: [
          {
            change: 'BECMG',
            valid: {
              start: dateAdd(upcomingValidDateStart, 1),
              end: dateAdd(upcomingValidDateStart, 2),
            },
            visibility: { range: 1000, unit: 'M' },
            weather: { weather1: 'MIFG' as WeatherPhenomena },
            cloud: {
              cloud1: 'NSC' as NSC,
            },
          },
          {
            change: 'FM',
            valid: {
              start: dateAdd(upcomingValidDateStart, 2),
            },
            visibility: { range: 6000, unit: 'M' },
            weather: { weather1: '+SHRA' as WeatherPhenomena },
            cloud: {
              cloud1: { coverage: 'BKN', height: 40 },
              cloud2: { coverage: 'OVC', height: 60, type: 'CB' },
            },
          },
        ],
      },
    }),
  ),
  withLocationDates(
    'EHFD',
    ({ upcomingValidDateStart, upcomingValidDateEnd }) => ({
      creationDate: upcomingValidDateStart,
      canbe: ['CANCELLED', 'AMENDED', 'CORRECTED'],
      editor: null!,
      taf: {
        uuid: '5f9583fjdf45ff4330f337925',
        type: 'NAF',
        issueDate: upcomingValidDateStart,
        baseTime: upcomingValidDateStart,
        validDateStart: upcomingValidDateStart,
        validDateEnd: upcomingValidDateEnd,
        location: 'EHFD',
        status: 'PUBLISHED',
        messageType: 'ORG',
        baseForecast: {
          valid: {
            start: upcomingValidDateStart,
            end: upcomingValidDateEnd,
          },
          visibility: { range: 600, unit: 'M' },
          wind: { direction: 180, speed: 10, unit: 'KT' },
          weather: { weather1: 'SN' as WeatherPhenomena },
          cloud: { cloud1: { coverage: 'BKN', height: 35 } },
        },
        changeGroups: [
          {
            change: 'TEMPO',
            probability: 'PROB30',
            valid: {
              start: dateAdd(upcomingValidDateStart, 1),
              end: dateAdd(upcomingValidDateStart, 2),
            },
            visibility: { range: 200, unit: 'M' },
            cloud: {
              cloud1: { verticalVisibility: 5 },
            },
          },
        ],
      },
    }),
  ),
  withLocationDates(
    'EHHW',
    ({ upcomingValidDateStart, upcomingValidDateEnd }) => ({
      creationDate: upcomingValidDateStart,
      canbe: ['AMENDED'],
      editor: null!,
      taf: {
        uuid: '5464565464',
        type: 'NAF',
        previousId: '5445fghrfg3546456',
        issueDate: upcomingValidDateStart,
        baseTime: upcomingValidDateStart,
        validDateStart: upcomingValidDateStart,
        validDateEnd: upcomingValidDateEnd,
        previousValidDateStart: upcomingValidDateStart,
        previousValidDateEnd: upcomingValidDateEnd,
        location: 'EHHW',
        status: 'CANCELLED',
        messageType: 'CNL',
        baseForecast: {
          valid: {
            start: upcomingValidDateStart,
            end: upcomingValidDateEnd,
          },
          visibility: { range: 600, unit: 'M' },
          wind: { direction: 180, speed: 10, unit: 'KT' },
          weather: { weather1: 'SN' as WeatherPhenomena },
          cloud: { cloud1: { coverage: 'BKN', height: 35 } },
        },
      },
    }),
  ),
  withLocationDates(
    'EHMA',
    ({ upcomingValidDateStart, upcomingValidDateEnd }) => ({
      creationDate: upcomingValidDateStart,
      canbe: ['CANCELLED', 'AMENDED', 'CORRECTED'],
      editor: 'max.jansen',
      taf: {
        uuid: 'gh56734fghfgh',
        type: 'NAF',
        previousId: '8574jnf93475',
        issueDate: `${currentDate}T16:16:00Z`,
        baseTime: upcomingValidDateStart,
        validDateStart: upcomingValidDateStart,
        validDateEnd: upcomingValidDateEnd,
        previousValidDateStart: upcomingValidDateStart,
        previousValidDateEnd: upcomingValidDateEnd,
        location: 'EHMA',
        status: 'AMENDED',
        messageType: 'AMD',
        baseForecast: {
          valid: {
            start: `${currentDate}T16:12:00Z`,
            end: upcomingValidDateEnd,
          },
          visibility: { range: 600, unit: 'M' },
          wind: { direction: 180, speed: 10, unit: 'KT' },
          weather: { weather1: 'SN' as WeatherPhenomena },
          cloud: { cloud1: { coverage: 'BKN', height: 35 } },
        },
      },
    }),
  ),
  withLocationDates(
    'EHQN',
    ({ currentValidDateStart, currentValidDateEnd }) => ({
      creationDate: currentValidDateStart,
      canbe: ['CANCELLED', 'AMENDED', 'CORRECTED'],
      editor: null!,
      taf: {
        uuid: 'giskdj09845872039',
        type: 'NAF',
        issueDate: currentValidDateStart,
        baseTime: currentValidDateStart,
        validDateStart: currentValidDateStart,
        validDateEnd: currentValidDateEnd,
        location: 'EHQN',
        status: 'PUBLISHED',
        messageType: 'ORG',
        baseForecast: {
          valid: {
            start: currentValidDateStart,
            end: currentValidDateEnd,
          },
          visibility: { range: 500, unit: 'M' },
          wind: { direction: 200, speed: 20, gust: 35, unit: 'KT' },
          weather: { weather1: 'SN' as WeatherPhenomena },
          cloud: { cloud1: { coverage: 'BKN', height: 40 } },
        },
      },
    }),
  ),
  withLocationDates(
    'EHPG',
    ({ currentValidDateStart, currentValidDateEnd }) => ({
      creationDate: currentValidDateStart,
      canbe: ['DRAFTED', 'DISCARDED', 'PUBLISHED'],
      editor: null!,
      taf: {
        uuid: 'ghskdj09845872039',
        type: 'NAF',
        baseTime: currentValidDateStart,
        validDateStart: currentValidDateStart,
        validDateEnd: currentValidDateEnd,
        location: 'EHPG',
        status: 'NEW',
        messageType: 'ORG',
        baseForecast: {
          valid: {
            start: currentValidDateStart,
            end: currentValidDateEnd,
          },
        },
      },
    }),
  ),
  withLocationDates(
    'EHDV',
    ({ currentValidDateStart, currentValidDateEnd }) => ({
      creationDate: currentValidDateStart,
      canbe: ['DRAFTED', 'DISCARDED', 'PUBLISHED'],
      editor: null!,
      taf: {
        uuid: 'eHDV123456789',
        type: 'NAF',
        baseTime: currentValidDateStart,
        validDateStart: currentValidDateStart,
        validDateEnd: currentValidDateEnd,
        location: 'EHDV',
        status: 'NEW',
        messageType: 'ORG',
        baseForecast: {
          valid: {
            start: currentValidDateStart,
            end: currentValidDateEnd,
          },
          wind: { direction: 270, speed: 15, unit: 'KT' },
          cavOK: true,
        },
      },
    }),
  ),
  // Added missing NafLocation: EHMG
  withLocationDates(
    'EHMG',
    ({ currentValidDateStart, currentValidDateEnd }) => ({
      creationDate: currentValidDateStart,
      canbe: ['DRAFTED', 'DISCARDED', 'PUBLISHED'],
      editor: null!,
      taf: {
        uuid: 'eHMG987654321',
        type: 'NAF',
        baseTime: currentValidDateStart,
        validDateStart: currentValidDateStart,
        validDateEnd: currentValidDateEnd,
        location: 'EHMG',
        status: 'NEW',
        messageType: 'ORG',
        baseForecast: {
          valid: {
            start: currentValidDateStart,
            end: currentValidDateEnd,
          },
          visibility: { range: 8000, unit: 'M' },
          wind: { direction: 90, speed: 8, unit: 'KT' },
          cloud: { cloud1: { coverage: 'SCT', height: 25 } },
        },
      },
    }),
  ),
];

export const fakeFixedNaf: TafFromBackend = {
  creationDate: `2022-01-06T12:00:00Z`,
  canbe: ['DRAFTED', 'DISCARDED', 'PUBLISHED'],
  editor: MOCK_USERNAME,
  taf: {
    messageType: 'ORG',
    status: 'NEW',
    uuid: '5f9583fjdf0445332f337925',
    baseTime: `2022-01-06T11:00:00Z`,
    validDateStart: `2022-01-06T11:00:00Z`,
    validDateEnd: `2022-01-06T20:00:00Z`,
    location: 'EHAK',
    type: 'NAF',
    baseForecast: {
      valid: {
        start: `2022-01-06T11:00:00Z`,
        end: `2022-01-06T20:00:00Z`,
      },
    },
  },
};

export const fakeLastInDayNaf: TafFromBackend = {
  creationDate: `2022-01-06T12:00:00Z`,
  canbe: ['DRAFTED', 'DISCARDED', 'PUBLISHED'],
  editor: MOCK_USERNAME,
  taf: {
    messageType: 'ORG',
    status: 'NEW',
    uuid: '5f9583fjdf0445332f337925',
    baseTime: `2022-01-06T14:00:00Z`,
    validDateStart: `2022-01-06T14:00:00Z`,
    validDateEnd: `2022-01-06T20:00:00Z`,
    location: 'EHAK',
    type: 'NAF',
    baseForecast: {
      valid: {
        start: `2022-01-06T14:00:00Z`,
        end: `2022-01-06T20:00:00Z`,
      },
    },
  },
};

// Fixed NAF objects for testing with fixed dates
export const fakeNewFixedNaf: TafFromBackend = {
  creationDate: `2022-01-06T12:00:00Z`,
  canbe: ['DRAFTED', 'DISCARDED', 'PUBLISHED'],
  editor: MOCK_USERNAME,
  taf: {
    messageType: 'ORG',
    status: 'NEW',
    uuid: '5f9583fjdf0445332f337925',
    baseTime: `2022-01-06T11:00:00Z`,
    validDateStart: `2022-01-06T11:00:00Z`,
    validDateEnd: `2022-01-06T20:00:00Z`,
    location: 'EHAK',
    type: 'NAF',
    baseForecast: {
      valid: {
        start: `2022-01-06T11:00:00Z`,
        end: `2022-01-06T20:00:00Z`,
      },
    },
  },
};

export const fakeDraftFixedNaf: TafFromBackend = {
  creationDate: `2022-01-06T14:00:00Z`,
  canbe: ['DRAFTED', 'DISCARDED', 'PUBLISHED'],
  editor: null!,
  taf: {
    uuid: '5f9583fjd111137925',
    type: 'NAF',
    baseTime: `2022-01-06T14:00:00Z`,
    validDateStart: `2022-01-06T14:00:00Z`,
    validDateEnd: `2022-01-06T20:00:00Z`,
    location: 'EHFD',
    status: 'DRAFT',
    messageType: 'ORG',
    baseForecast: {
      valid: {
        start: `2022-01-06T14:00:00Z`,
        end: `2022-01-06T20:00:00Z`,
      },
      wind: { direction: 180, speed: 10, unit: 'KT' },
      cavOK: true,
    },
  },
};

export const fakePublishedFixedNaf: TafFromBackend = {
  creationDate: `2022-01-06T14:00:00Z`,
  canbe: ['CANCELLED', 'AMENDED', 'CORRECTED'],
  editor: null!,
  taf: {
    uuid: '5f9583fjdf45ff4330f337925',
    type: 'NAF',
    issueDate: `2022-01-06T14:00:00Z`,
    baseTime: `2022-01-06T14:00:00Z`,
    validDateStart: `2022-01-06T14:00:00Z`,
    validDateEnd: `2022-01-06T20:00:00Z`,
    location: 'EHFD',
    status: 'PUBLISHED',
    messageType: 'ORG',
    baseForecast: {
      valid: {
        start: `2022-01-06T14:00:00Z`,
        end: `2022-01-06T20:00:00Z`,
      },
      visibility: { range: 600, unit: 'M' },
      wind: { direction: 180, speed: 10, unit: 'KT' },
      weather: { weather1: 'SN' as WeatherPhenomena },
      cloud: { cloud1: { coverage: 'BKN', height: 35 } },
    },
    changeGroups: [
      {
        change: 'TEMPO',
        probability: 'PROB30',
        valid: {
          start: `2022-01-06T14:00:00Z`,
          end: `2022-01-06T20:00:00Z`,
        },
        visibility: { range: 200, unit: 'M' },
        cloud: {
          cloud1: { verticalVisibility: 5 },
        },
      },
    ],
  },
};

export const fakeDraftAmendmentFixedNaf: TafFromBackend = {
  creationDate: `2022-01-06T14:00:00Z`,
  canbe: ['DRAFTED', 'DISCARDED', 'PUBLISHED'],
  editor: null!,
  taf: {
    uuid: '754905485',
    type: 'NAF',
    baseTime: `2022-01-06T14:00:00Z`,
    previousId: '5f9583fjdf45ff4330f337925',
    validDateStart: `2022-01-06T14:00:00Z`,
    validDateEnd: `2022-01-06T20:00:00Z`,
    previousValidDateStart: `2022-01-06T11:00:00Z`,
    previousValidDateEnd: `2022-01-06T20:00:00Z`,
    location: 'EHKV',
    status: 'DRAFT_AMENDED',
    messageType: 'AMD',
    baseForecast: {
      valid: {
        start: `2022-01-06T14:00:00Z`,
        end: `2022-01-06T20:00:00Z`,
      },
      visibility: { range: 600, unit: 'M' },
      wind: { direction: 180, speed: 10, unit: 'KT' },
      weather: { weather1: 'SN' as WeatherPhenomena },
      cloud: { cloud1: { coverage: 'BKN', height: 35 } },
    },
    changeGroups: [
      {
        change: 'TEMPO',
        probability: 'PROB30',
        valid: {
          start: `2022-01-06T14:00:00Z`,
          end: `2022-01-06T20:00:00Z`,
        },
        visibility: { range: 100, unit: 'M' },
        cloud: {
          cloud1: { verticalVisibility: 5 },
        },
      },
    ],
  },
};

export const fakeAmendmentFixedNaf: TafFromBackend = {
  creationDate: `2022-01-06T14:00:00Z`,
  canbe: ['CANCELLED', 'AMENDED', 'CORRECTED'],
  editor: null!,
  taf: {
    uuid: '754905486',
    type: 'NAF',
    previousId: '5f9583fjdf45ff4330f337925',
    issueDate: `2022-01-06T14:00:00Z`,
    baseTime: `2022-01-06T14:00:00Z`,
    validDateStart: `2022-01-06T14:00:00Z`,
    validDateEnd: `2022-01-06T20:00:00Z`,
    previousValidDateStart: `2022-01-06T14:00:00Z`,
    previousValidDateEnd: `2022-01-06T20:00:00Z`,
    location: 'EHMA',
    status: 'AMENDED',
    messageType: 'AMD',
    baseForecast: {
      valid: {
        start: `2022-01-06T14:00:00Z`,
        end: `2022-01-06T20:00:00Z`,
      },
      visibility: { range: 600, unit: 'M' },
      wind: { direction: 180, speed: 10, unit: 'KT' },
      weather: { weather1: 'SN' as WeatherPhenomena },
      cloud: { cloud1: { coverage: 'BKN', height: 35 } },
    },
  },
};

export const fakeCancelledFixedNaf: TafFromBackend = {
  creationDate: `2022-01-06T12:00:00Z`,
  canbe: ['AMENDED'],
  editor: null!,
  taf: {
    uuid: '5464565464',
    type: 'NAF',
    previousId: '5445fghrfg3546456',
    issueDate: `2022-01-06T11:00:00Z`,
    baseTime: `2022-01-06T11:00:00Z`,
    validDateStart: `2022-01-06T11:00:00Z`,
    validDateEnd: `2022-01-06T20:00:00Z`,
    previousValidDateStart: `2022-01-06T11:00:00Z`,
    previousValidDateEnd: `2022-01-06T20:00:00Z`,
    location: 'EHHW',
    status: 'CANCELLED',
    messageType: 'CNL',
    baseForecast: {
      valid: {
        start: `2022-01-06T11:00:00Z`,
        end: `2022-01-06T20:00:00Z`,
      },
      visibility: { range: 600, unit: 'M' },
      wind: { direction: 180, speed: 10, unit: 'KT' },
      weather: { weather1: 'SN' as WeatherPhenomena },
      cloud: { cloud1: { coverage: 'BKN', height: 35 } },
    },
  },
};

// Fixed NAF list similar to fakeFixedTafList
export const fakeFixedNafList = [
  fakeAmendmentFixedNaf,
  fakeDraftAmendmentFixedNaf,
  fakeDraftFixedNaf,
  fakePublishedFixedNaf,
  fakeNewFixedNaf,
  fakeCancelledFixedNaf,
];
